import dayjs, { Dayjs } from 'dayjs';
import { LabeledValue, SelectValue } from 'antd/lib/select';

import { useAppSelector } from 'hooks/store-hooks';
import { FilterReturnType } from 'hooks/use-filter';
import wimSelectors from 'store/wim/selectors';
import { TransitsArchiveFilterValues, TransitsFilterValues } from 'store/wim/types/transits';
import { PLATE_FIELDS } from 'app-constants/plate-fields';
import { MEASUREMENT_VALIDITY, OPTION_KEYS_FOR_SELECT } from 'app-constants/select-options';
import { VIOLATIONS_TYPES } from 'app-constants/violations';
import { useGetMonitoringMetrics } from 'pages/Wim/Monitoring/WimMonitoringFilter/hooks/use-get-monitoring-metrics';
import adminSelectors from 'store/admin/selectors';
import { WAGA_VEHICLE_TYPES } from '../../../../app-constants/waga-vehicle-types';

export function useTransitsFilter<TransitsType extends TransitsFilterValues>({
  state,
  handleFilterChange,
  onRemoveFields,
}: Pick<FilterReturnType<Partial<TransitsType>>, 'handleFilterChange' | 'state' | 'onRemoveFields'>) {
  const platforms = useAppSelector(wimSelectors.platforms.getPlatforms);
  useGetMonitoringMetrics();
  const metrics = useAppSelector(wimSelectors.monitoring.getWimMonitoringMetrics) ?? [];
  const lanes = useAppSelector(adminSelectors.lanes.getLanes) ?? [];
  const onPlatformNameChange = (value: SelectValue) => {
    if (value && Array.isArray(value)) {
      if (value.includes(OPTION_KEYS_FOR_SELECT.All as string & LabeledValue)) {
        handleFilterChange(
          'platformUids',
          platforms?.map((platform) => platform.uid)
        );
      } else {
        handleFilterChange('platformUids', value);
      }
    }
  };

  const onRangePickerChange = (ranges: [Dayjs | null, Dayjs | null] | null) => {
    if (!ranges) {
      return;
    }

    const [start, end] = ranges;

    if (start && end) {
      handleFilterChange('start', dayjs(start).valueOf());
      handleFilterChange('end', dayjs(end).valueOf());
    }
  };

  const onClassificationTypesChange = (value: SelectValue) => {
    if (value && Array.isArray(value)) {
      if (value.includes(OPTION_KEYS_FOR_SELECT.All as string & LabeledValue)) {
        handleFilterChange(
          'vehicleClasses',
          Object.keys(WAGA_VEHICLE_TYPES).map((cId) => WAGA_VEHICLE_TYPES[cId].id)
        );
      } else {
        handleFilterChange('vehicleClasses', value);
      }
    }
  };

  const onLanesChange = (value: SelectValue) => {
    if (value && Array.isArray(value)) {
      if (value.includes(OPTION_KEYS_FOR_SELECT.All as string & LabeledValue)) {
        handleFilterChange(
          'laneIds',
          lanes.map((lane: string) => lane)
        );
      } else {
        handleFilterChange('laneIds', value);
      }
    }
  };

  const onTransitErrorChange = (value: SelectValue) => {
    if (value && Array.isArray(value)) {
      if (value.includes(OPTION_KEYS_FOR_SELECT.All as string & LabeledValue)) {
        handleFilterChange(
          'measurementFlags',
          metrics.map((metric) => metric?.filters?.measurementFlags?.[0])
        );
      } else {
        handleFilterChange('measurementFlags', value);
      }
    }
  };

  const getMeasurementValidity = (): MEASUREMENT_VALIDITY | undefined => {
    if (state.vendorErrorsPresence === true) {
      return MEASUREMENT_VALIDITY.WithErrors;
    }
    if (state.vendorErrorsPresence === false) {
      return MEASUREMENT_VALIDITY.WithoutErrors;
    }
    return undefined;
  };

  const onMeasurementValidityChange = (value?: MEASUREMENT_VALIDITY) => {
    if (!value) {
      onRemoveFields('vendorErrorsPresence');
      return;
    }
    if (value === MEASUREMENT_VALIDITY.WithoutErrors) {
      // onRemoveFields replaces state with a snapshot, so it must run before the functional update below.
      onRemoveFields('measurementFlags');
    }
    handleFilterChange('vendorErrorsPresence', value === MEASUREMENT_VALIDITY.WithErrors);
  };

  const onViolationsTypesChange = (value: SelectValue) => {
    if (value && Array.isArray(value)) {
      if (value.includes(OPTION_KEYS_FOR_SELECT.All as string & LabeledValue)) {
        handleFilterChange(
          'violationsTypesByOr',
          Object.values(VIOLATIONS_TYPES).map((violation) => violation.id)
        );
      } else {
        handleFilterChange('violationsTypesByOr', value);
      }
    }
  };

  const getOnPlateChange =
    (field: PLATE_FIELDS.Plate | PLATE_FIELDS.PlateBack) =>
    ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      const recognizedFieldName =
        field === PLATE_FIELDS.Plate ? PLATE_FIELDS.PlateRecognized : PLATE_FIELDS.PlateBackRecognized;
      const { value } = target;
      handleFilterChange(field, value);
      handleFilterChange(recognizedFieldName, true);
      if (value === null || value === '') {
        onRemoveFields(field);
      }
    };

  const getOnSliderChange =
    (startField: keyof TransitsArchiveFilterValues, endField: keyof TransitsArchiveFilterValues) =>
    ([startValue, endValue]: [number, number]) => {
      handleFilterChange(startField, startValue);
      handleFilterChange(endField, endValue);
      if (startValue === null && endValue) {
        onRemoveFields(startField);
      }
      if (!startValue && !endValue) {
        onRemoveFields(startField, endField);
      }
    };

  return {
    state,
    handleFilterChange,
    platforms,
    onPlatformNameChange,
    onRangePickerChange,
    onViolationsTypesChange,
    getOnPlateChange,
    getOnSliderChange,
    onClassificationTypesChange,
    onLanesChange,
    lanes,
    onTransitErrorChange,
    metrics,
    measurementValidity: getMeasurementValidity(),
    onMeasurementValidityChange,
  };
}
